import React, { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { UI, inputClass } from '../../utils/styles'
import { getReadingHelperText } from '../../utils/constants'
import { normalizeReading } from '../../utils/calculations'
import { ButtonPrimary } from '../ui/Button'
import { X } from '../ui/Icons'
import { meterUnits } from '../../utils/constants'
import { calculateInches, calculateAcreInches } from '../../utils/calculations'

export function AuthScreen({ onAuthed, pendingInviteCode, redeemInviteCode }) {
  const [mode, setMode] = useState(pendingInviteCode ? "signup" : "login")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [firstName, setFirstName] = useState("")
  const [zipCode, setZipCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [inviteOperationName, setInviteOperationName] = useState("")
  const [showCalculatorOnly, setShowCalculatorOnly] = useState(false)
  const [calcStart, setCalcStart] = useState("")
  const [calcEnd, setCalcEnd] = useState("")
  const [calcAcres, setCalcAcres] = useState("")
  const [calcUnits, setCalcUnits] = useState("Gallons × 100")
  const [calcResult, setCalcResult] = useState(null)

  useEffect(() => {
    if (!pendingInviteCode) return
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from("operation_invites")
          .select("operation_id, operation_name")
          .eq("invite_code", pendingInviteCode)
          .single()
        if (!error && data?.operation_name) {
          setInviteOperationName(data.operation_name)
        }
      } catch (e) {
        console.error("Failed to fetch operation name:", e)
      }
    })()
  }, [pendingInviteCode])

  const doSignup = async () => {
    setBusy(true)
    setError("")
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName || null,
          zip_code: zipCode || null
        }
      }
    })
    if (error) {
      setError(error.message)
      setBusy(false)
      return
    }

    if (pendingInviteCode && data?.user) {
      try {
        await redeemInviteCode(data.user.id, pendingInviteCode)
        window.location.href = window.location.origin
        return
      } catch (redeemErr) {
        console.error("Failed to redeem invite:", redeemErr)
        setError("Account created, but failed to join operation. Please try again.")
        setBusy(false)
        return
      }
    }

    const session = data?.session || null
    if (session) onAuthed(session)
    else setError("Signup created. If you turned email confirmation ON, check your inbox.")
    setBusy(false)
  }

  const doLogin = async () => {
    setBusy(true)
    setError("")
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.message)
      setBusy(false)
      return
    }

    if (pendingInviteCode && data?.user) {
      try {
        await redeemInviteCode(data.user.id, pendingInviteCode)
        window.location.href = window.location.origin
        return
      } catch (redeemErr) {
        console.error("Failed to redeem invite:", redeemErr)
      }
    }

    onAuthed(data.session)
    setBusy(false)
  }

  const handleCalculate = () => {
    if (calcStart && calcEnd && calcAcres) {
      const startVal = normalizeReading(calcStart, calcUnits)
      const endVal = normalizeReading(calcEnd, calcUnits)
      const acresVal = parseFloat(calcAcres) || 0

      let reading = endVal - startVal

      if (reading < 0) {
        const digits = startVal.toString().split('.')[0].length
        const maxVal = Math.pow(10, digits) - 1
        reading = (maxVal - startVal) + endVal + 1
      }

      let totalGallons = 0
      let acreInches = 0

      switch(calcUnits) {
        case 'Gallons × 1':
          totalGallons = reading
          acreInches = totalGallons / GAL_PER_ACRE_INCH
          break
        case 'Gallons × 10':
          totalGallons = reading * 10
          acreInches = totalGallons / GAL_PER_ACRE_INCH
          break
        case 'Gallons × 100':
          totalGallons = reading * 100
          acreInches = totalGallons / GAL_PER_ACRE_INCH
          break
        case 'Gallons × 1000':
          totalGallons = reading * 1000
          acreInches = totalGallons / GAL_PER_ACRE_INCH
          break
        case 'Acre-Inches × 0.001':
          acreInches = reading * 0.001
          totalGallons = acreInches * GAL_PER_ACRE_INCH
          break
        case 'Acre-Inches × 0.01':
          acreInches = reading * 0.01
          totalGallons = acreInches * GAL_PER_ACRE_INCH
          break
        case 'Acre-Feet × 0.001':
          acreInches = reading * 0.001 * 12
          totalGallons = acreInches * GAL_PER_ACRE_INCH
          break
        default:
          totalGallons = reading * 100
          acreInches = totalGallons / GAL_PER_ACRE_INCH
      }

      const inches = acresVal > 0 ? (acreInches / acresVal) : 0
      const totalAcreInches = inches * acresVal

      setCalcResult({
        inches: inches.toFixed(2),
        acreInches: totalAcreInches.toFixed(1)
      })
    }
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center p-4 md:p-6">
        <div className={`${UI.glass} ${UI.shadow} ${UI.radius} p-6 max-w-md w-full`}>
          {pendingInviteCode && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-center">
              <div className="text-sm font-semibold text-slate-800 mb-1">
                You have been invited to {inviteOperationName || "an operation"}
              </div>
              <div className="text-xs text-slate-700">
                {mode === "signup" ? "Sign up below to join!" : "Log in or sign up to join!"}
              </div>
            </div>
          )}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-semibold text-slate-800 mb-3 tracking-tight">MeterCalc</h1>
            <p className="text-slate-600">{mode === "login" ? "Log in to your account" : "Create your account"}</p>
          </div>

          <div className="space-y-5">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1.5">First Name</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} type="text" className={inputClass} placeholder="John" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1.5">Zip Code</label>
                  <input value={zipCode} onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ''))} type="text" inputMode="numeric" pattern="[0-9]*" className={inputClass} placeholder="12345" maxLength="5" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1.5">Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={inputClass} placeholder="you@email.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1.5">Password</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className={inputClass} placeholder="" />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-900 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <ButtonPrimary
              onClick={mode === "login" ? doLogin : doSignup}
              disabled={busy || !email || !password}
              className="w-full"
            >
              {busy ? "Working..." : (mode === "login" ? "Log In" : "Create Account")}
            </ButtonPrimary>

            <button
              className="w-full text-sm text-sky-700 underline py-3"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
            >
              {mode === "login"
                ? "Need an account? Sign up"
                : pendingInviteCode
                  ? `Already have an account? Log in to join ${inviteOperationName || "operation"}`
                  : "Already have an account? Log in"}
            </button>

            <div className="mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={() => setShowCalculatorOnly(true)}
                className="w-full text-sm text-sky-700 hover:text-sky-800 py-2 transition-colors"
              >
                Try Calculator Only
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCalculatorOnly && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className={`bg-white ${UI.radius} shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto relative animate-modal-in`}>
            <button
              onClick={() => {
                setShowCalculatorOnly(false)
                setCalcStart("")
                setCalcEnd("")
                setCalcAcres("")
                setCalcUnits("Gallons × 100")
                setCalcResult(null)
              }}
              className="absolute top-4 right-4 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <X />
            </button>

            <h2 className="text-xl font-bold text-slate-800 mb-4 pr-8">Irrigation Calculator</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1.5">Previous Reading</label>
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={calcStart}
                    onChange={(e) => setCalcStart(e.target.value)}
                    className={inputClass}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1.5">Current Reading</label>
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={calcEnd}
                    onChange={(e) => setCalcEnd(e.target.value)}
                    className={inputClass}
                    placeholder="5000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Acres</label>
                <input
                  type="number"
                  value={calcAcres}
                  onChange={(e) => setCalcAcres(e.target.value)}
                  className={inputClass}
                  placeholder="130"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Meter Units</label>
                <select
                  value={calcUnits}
                  onChange={(e) => setCalcUnits(e.target.value)}
                  className={inputClass}
                >
                  {meterUnits.map(unit => (<option key={unit} value={unit}>{unit}</option>))}
                </select>
                {getReadingHelperText(calcUnits) && (
                  <p className="mt-1.5"><span className="text-sm font-medium text-gray-500">{'\u2192'} </span><span className="text-sm text-gray-400 font-normal">{getReadingHelperText(calcUnits)}</span></p>
                )}
              </div>

              <ButtonPrimary
                onClick={handleCalculate}
                disabled={!calcStart || !calcEnd || !calcAcres}
                className="w-full"
              >
                Calculate
              </ButtonPrimary>

              {calcResult && (
                <>
                  <div className={`${UI.glass} ${UI.border} ${UI.radius} p-4 space-y-3`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className="text-xs font-medium text-slate-600 mb-1">Inches Applied</p>
                        <p className="text-3xl font-bold text-slate-800">{calcResult.inches}"</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-slate-600 mb-1">Acre-Inches</p>
                        <p className="text-3xl font-bold text-slate-800">{calcResult.acreInches}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 space-y-3">
                    <p className="text-sm text-slate-700 text-center">Want to track your irrigation and save readings?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <ButtonPrimary
                        onClick={() => {
                          setShowCalculatorOnly(false)
                          setMode("signup")
                        }}
                        className="text-sm py-2"
                      >
                        Sign Up
                      </ButtonPrimary>
                      <ButtonSecondary
                        onClick={() => {
                          setShowCalculatorOnly(false)
                          setMode("login")
                        }}
                        className="text-sm !py-2"
                      >
                        Sign In
                      </ButtonSecondary>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
